import { useEffect, useState } from 'react';
import { identityService } from '../services/identityService';
import { UserBlock } from '../types/user-block';

/**
 * Outcome of the block lookup. `error` exists on purpose: a failed lookup used to fall through to an unchecked
 * "Blocked" box, which is indistinguishable from a healthy "not blocked" answer and silently tells the support agent
 * the account is fine. The caller must render `error` differently from `notBlocked`.
 */
export type UserBlockStatus = 'loading' | 'blocked' | 'notBlocked' | 'error';

export interface UserBlockState {
  status: UserBlockStatus;
  /** The reasons the user is blocked; only populated when `status` is `blocked`. */
  blocks: UserBlock[];
}

/**
 * Loads the identity's active blocks: one entry per login-preventing FusionAuth user action. An empty list means
 * "not blocked".
 */
export const useUserBlockStatus = (identityId?: string): UserBlockState => {
  const [state, setState] = useState<UserBlockState>({ status: 'loading', blocks: [] });

  useEffect(() => {
    if (!identityId) {
      setState({ status: 'loading', blocks: [] });
      return;
    }

    // Guards against a slower response for a previous identity overwriting the current one.
    let cancelled = false;
    setState({ status: 'loading', blocks: [] });

    identityService
      .getUserBlockList(identityId)
      .then((blocks) => {
        if (cancelled) return;
        setState({ status: blocks.length > 0 ? 'blocked' : 'notBlocked', blocks });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch user block list', err);
        setState({ status: 'error', blocks: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [identityId]);

  return state;
};
